import React, { useState, useEffect } from 'react';
import { X, Clock, Ban, CheckCircle, XCircle, MapPin, User, Car, Calendar } from 'lucide-react';
import { useQuery } from 'react-query';
import { api } from '../utils/api';

const HistoryModal = ({ isOpen, onClose, type, driverId, driverName }) => {
  const [activeTab, setActiveTab] = useState('block');
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const { data: blockHistory } = useQuery(
    ['driver-block-history', driverId],
    () => api.get(`/drivers/${driverId}/block-history`),
    {
      enabled: isOpen && !!driverId && activeTab === 'block',
      select: (data) => data?.data?.data || data?.data
    }
  );

  const { data: tripHistory } = useQuery(
    ['driver-trips', driverId],
    () => api.get(`/drivers/${driverId}/trips`),
    {
      enabled: isOpen && !!driverId && activeTab === 'trip',
      select: (data) => data?.data?.data || data?.data
    }
  );

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'ASSIGNED': { color: 'badge-info', text: 'Assigned' },
      'STARTED': { color: 'badge-primary', text: 'Started' },
      'IN_PROGRESS': { color: 'badge-warning', text: 'In Progress' },
      'COMPLETED': { color: 'badge-success', text: 'Completed' },
      'CANCELLED': { color: 'badge-danger', text: 'Cancelled' },
      'DROPPED': { color: 'badge-success', text: 'Dropped' }
    };
    
    const config = statusConfig[status] || { color: 'badge-secondary', text: status };
    return <span className={`badge ${config.color}`}>{config.text}</span>;
  };

  const getActionIcon = (action) => {
    return action === 'blocked' ? <Ban className="h-4 w-4 text-red-600" /> : <CheckCircle className="h-4 w-4 text-green-600" />;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {driverName} - History
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-6 border-b">
          <button
            onClick={() => setActiveTab('block')}
            className={`pb-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'block'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Ban className="h-4 w-4 inline mr-2" />
            Block History
          </button>
          <button
            onClick={() => setActiveTab('trip')}
            className={`pb-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'trip'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <MapPin className="h-4 w-4 inline mr-2" />
            Trip History
          </button>
        </div>

        {activeTab === 'block' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Block History</h3>
            {blockHistory?.blockHistory && blockHistory.blockHistory.length > 0 ? (
              <div className="space-y-3">
                {blockHistory.blockHistory.map((entry) => (
                  <div
                    key={entry._id}
                    className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedItem(entry)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {getActionIcon(entry.action)}
                        <div>
                          <p className="font-medium text-gray-900">
                            {entry.action === 'blocked' ? 'Blocked' : 'Unblocked'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatTime(entry.blockedAt || entry.unblockedAt)}
                          </p>
                          <p className="text-xs text-gray-400">
                            by {entry.adminName || 'System'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        {entry.blockType && (
                          <span className={`badge ${entry.blockType === 'permanent' ? 'badge-danger' : 'badge-warning'}`}>
                            {entry.blockType}
                          </span>
                        )}
                      </div>
                    </div>
                    {entry.reason && (
                      <p className="text-sm text-gray-600 mt-2">
                        <strong>Reason:</strong> {entry.reason}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Ban className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No block history found</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'trip' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Trip History</h3>
            {tripHistory?.trips && tripHistory.trips.length > 0 ? (
              <div className="space-y-3">
                {tripHistory.trips.map((trip) => (
                  <div
                    key={trip._id}
                    className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedItem(trip)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <MapPin className="h-4 w-4 text-blue-600" />
                        <div>
                          <p className="font-medium text-gray-900">
                            Trip ID : {trip._id}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        {getStatusBadge(trip.status)}
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Passenger:</span>
                        <p className="font-medium">{trip.bookingForName || trip.userId?.name || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Fare:</span>
                        <p className="font-medium">₹{trip.finalDistance ? (trip.finalDistance * 10).toFixed(2) : 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No trip history found</p>
              </div>
            )}
          </div>
        )}

        {/* Detail Modal */}
        {selectedItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {activeTab === 'block' ? 'Block Details' : 'Trip Details'}
                </h3>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {activeTab === 'block' && selectedItem && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Action</label>
                      <p className="text-sm text-gray-900 capitalize">{selectedItem.action}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Date</label>
                      <p className="text-sm text-gray-900">
                        {formatTime(selectedItem.blockedAt || selectedItem.unblockedAt)}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Admin</label>
                      <p className="text-sm text-gray-900">{selectedItem.adminName || 'System'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <p className="text-sm text-gray-900">{selectedItem.adminEmail || 'N/A'}</p>
                    </div>
                    {selectedItem.blockType && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Block Type</label>
                        <p className="text-sm text-gray-900 capitalize">{selectedItem.blockType}</p>
                      </div>
                    )}
                    {selectedItem.blockExpiry && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Expiry</label>
                        <p className="text-sm text-gray-900">{formatTime(selectedItem.blockExpiry)}</p>
                      </div>
                    )}
                  </div>
                  {selectedItem.reason && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Reason</label>
                      <p className="text-sm text-gray-900">{selectedItem.reason}</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'trip' && selectedItem && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Trip ID</label>
                      <p className="text-sm text-gray-900">{selectedItem._id}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <div className="mt-1">{getStatusBadge(selectedItem.status)}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Created</label>
                      <p className="text-sm text-gray-900">{formatTime(selectedItem.createdOn)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Distance</label>
                      <p className="text-sm text-gray-900">{selectedItem.finalDistance?.toFixed(2) || 'N/A'} km</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Duration</label>
                      <p className="text-sm text-gray-900">{selectedItem.finalDuration || 'N/A'} min</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Fare</label>
                      <p className="text-sm text-gray-900">₹{selectedItem.finalDistance ? (selectedItem.finalDistance * 10).toFixed(2) : 'N/A'}</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Passenger</label>
                    <p className="text-sm text-gray-900">{selectedItem.bookingForName || selectedItem.userId?.name || 'N/A'}</p>
                  </div>
                  
                  {selectedItem.stops && selectedItem.stops.length > 0 && (
                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-gray-800 mb-3">Route & Stops Timeline</label>
                      <div className="relative border-l-2 border-gray-200 ml-3 pl-6 space-y-6">
                        {selectedItem.stops.map((stop, index) => {
                          const isFirst = index === 0;
                          const isLast = index === selectedItem.stops.length - 1;
                          
                          // Determine circle color & border
                          let circleClass = "bg-yellow-100 border-yellow-500 text-yellow-700";
                          if (isFirst) {
                            circleClass = "bg-green-100 border-green-500 text-green-700";
                          } else if (isLast) {
                            circleClass = "bg-red-100 border-red-500 text-red-700";
                          }

                          const formattedTime = stop.arrivalTime ? new Date(stop.arrivalTime).toLocaleTimeString() : null;

                          return (
                            <div key={stop._id || index} className="relative">
                              {/* Timeline node circle */}
                              <span className={`absolute -left-[31px] top-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 bg-white text-[10px] font-bold ${circleClass}`}>
                                {index + 1}
                              </span>
                              
                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <h4 className="font-semibold text-gray-900 text-sm">
                                    {stop.name}
                                  </h4>
                                  {formattedTime && (
                                    <span className="text-xs text-gray-500 font-medium">
                                      {formattedTime}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 leading-relaxed">{stop.address || "No address provided"}</p>
                                
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {stop.waitingTime > 0 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-100">
                                      Passenger Wait: {stop.waitingTime} mins
                                    </span>
                                  )}
                                  {stop.driverWaitTime > 0 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                                      Driver Wait: {stop.driverWaitTime} mins
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryModal; 